import React, { createContext, useContext, useState, ReactNode, useCallback } from 'react';
import { WebsiteContent } from './WebsiteContentContext';

interface PreviewContextType {
  previewContent: WebsiteContent | null;
  isPreviewMode: boolean;
  isPreviewOpen: boolean;
  isShowingDraftChanges: boolean;
  updatePreviewSection: (section: keyof WebsiteContent, data: any) => void;
  togglePreviewMode: () => void;
  openPreview: () => void;
  closePreview: () => void;
  resetPreview: () => void;
  exportPreview: () => void;
  initializePreview: (content: WebsiteContent) => void;
  showDraftChanges: (draftContent: WebsiteContent) => void;
  showSavedContent: () => void;
  toggleDraftPreview: (savedContent: WebsiteContent, draftContent: WebsiteContent) => void;
}

const PreviewContext = createContext<PreviewContextType | undefined>(undefined);

export const usePreview = () => {
  const context = useContext(PreviewContext);
  if (!context) {
    throw new Error('usePreview must be used within PreviewProvider');
  }
  return context;
};

interface PreviewProviderProps {
  children: ReactNode;
}

export const PreviewProvider: React.FC<PreviewProviderProps> = ({ children }) => {
  const [previewContent, setPreviewContent] = useState<WebsiteContent | null>(null);
  const [isPreviewMode, setIsPreviewMode] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [isShowingDraftChanges, setIsShowingDraftChanges] = useState(false);
  const [savedContent, setSavedContent] = useState<WebsiteContent | null>(null);

  const initializePreview = useCallback((content: WebsiteContent) => {
    // Deep clone the content to avoid reference issues
    const clonedContent = JSON.parse(JSON.stringify(content));
    setPreviewContent(clonedContent);
    setSavedContent(clonedContent); // Store the saved content
    setIsShowingDraftChanges(false); // Reset to showing saved content
  }, []);

  const updatePreviewSection = useCallback((section: keyof WebsiteContent, data: any) => {
    setPreviewContent(prev => prev ? {
      ...prev,
      [section]: data,
    } : null);
  }, []);

  const togglePreviewMode = () => {
    setIsPreviewMode(prev => !prev);
  };

  const openPreview = () => {
    setIsPreviewOpen(true);
  };

  const closePreview = () => {
    setIsPreviewOpen(false);
    setIsPreviewMode(false);
    // Reset to showing saved content when closing
    if (savedContent) {
      setPreviewContent(JSON.parse(JSON.stringify(savedContent)));
      setIsShowingDraftChanges(false);
    }
  };

  const resetPreview = () => {
    setPreviewContent(null);
    setIsPreviewMode(false);
    setIsShowingDraftChanges(false);
    setSavedContent(null);
  };

  const showDraftChanges = useCallback((draftContent: WebsiteContent) => {
    setPreviewContent(JSON.parse(JSON.stringify(draftContent)));
    setIsShowingDraftChanges(true);
    setIsPreviewOpen(true);
  }, []);

  const showSavedContent = useCallback(() => {
    if (savedContent) {
      setPreviewContent(JSON.parse(JSON.stringify(savedContent)));
      setIsShowingDraftChanges(false);
    }
  }, [savedContent]);

  const toggleDraftPreview = useCallback((savedContentParam: WebsiteContent, draftContent: WebsiteContent) => {
    if (isShowingDraftChanges) {
      // Currently showing draft changes, switch to saved content
      showSavedContent();
    } else {
      // Currently showing saved content, switch to draft changes
      showDraftChanges(draftContent);
    }
  }, [isShowingDraftChanges, showSavedContent, showDraftChanges]);

  // Helper function to extract language-specific data
  const extractLanguageData = (data: any, lang: 'en' | 'ar'): any => {
    if (typeof data === 'string') return data;
    if (data && typeof data === 'object') {
      if ('en' in data && 'ar' in data) {
        return data[lang];
      }
      const result: any = {};
      for (const key in data) {
        result[key] = extractLanguageData(data[key], lang);
      }
      return result;
    }
    return data;
  };

  const exportPreview = () => {
    if (!previewContent) return;

    // Split preview content into two language files
    const enContent = extractLanguageData(previewContent, 'en');
    const arContent = extractLanguageData(previewContent, 'ar');

    // Download English file
    const enBlob = new Blob([JSON.stringify(enContent, null, 2)], {
      type: 'application/json',
    });
    const enUrl = URL.createObjectURL(enBlob);
    const enLink = document.createElement('a');
    enLink.href = enUrl;
    enLink.download = 'content-en-preview.json';
    document.body.appendChild(enLink);
    enLink.click();
    document.body.removeChild(enLink);
    URL.revokeObjectURL(enUrl);

    // Download Arabic file (with small delay)
    setTimeout(() => {
      const arBlob = new Blob([JSON.stringify(arContent, null, 2)], {
        type: 'application/json',
      });
      const arUrl = URL.createObjectURL(arBlob);
      const arLink = document.createElement('a');
      arLink.href = arUrl;
      arLink.download = 'content-ar-preview.json';
      document.body.appendChild(arLink);
      arLink.click();
      document.body.removeChild(arLink);
      URL.revokeObjectURL(arUrl);
    }, 100);
  };

  return (
    <PreviewContext.Provider
      value={{
        previewContent,
        isPreviewMode,
        isPreviewOpen,
        isShowingDraftChanges,
        updatePreviewSection,
        togglePreviewMode,
        openPreview,
        closePreview,
        resetPreview,
        exportPreview,
        initializePreview,
        showDraftChanges,
        showSavedContent,
        toggleDraftPreview,
      }}
    >
      {children}
    </PreviewContext.Provider>
  );
};